import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogContentText,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { GridCellParams, GridCheckIcon, GridColumns, GridRowClassNameParams } from "@mui/x-data-grid-pro";
import { ScreenHeader } from "common/ScreenHeader";
import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  FetchCommitteeMeetingToEnterDecisionsQuery,
  GenerateDecisionLettersMutation,
  GetMeetingsForFinalDecisionQuery,
  ToggleFinalReportReviewedMutation,
  ToggleTabledLetterReviewedMutation
} from ".";
import { CommitteeMeeting, CommitteeMeetingTabledReview, ReleaseFinalReportsForDirectorReviewMutation } from "committee-meetings";
import { makeStyles } from "makeStyles";
import { ApproveFinalReportsMutation, PracticeReview, PRBaseStatusCode } from "practice-reviews";
import useLocalStorage from "util/useLocalStorage";
import { completeableRowStyles, datagridStyles } from "styles/common";
import { useHistory, useLocation } from "react-router-dom";
import { getRouteForPracticeReview, PracticeReviewTabs } from "../practice-reviews/PracticeReviewScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { MicrosoftWordLink } from "../common/MicrosoftWordLink";
import { CommitteeMeetingRegretsDialog } from "./CommitteeMeetingRegretsDialog";
import { useNotifications } from "../notifications";
import { ConfirmationDialog } from "common/ConfirmationDialog";
import fileDownload from "js-file-download";
import { useAxios } from "../auth/SecureAxios";
import { useAppConfig } from "../util/AppConfig";
import { Link as RouterLink } from "react-router-dom";
import { DecisionTypeCode } from "../decisions";
import { AxiosResponse } from "axios";
import RegistrationCommitteeDecisionsTable from "registration-committee-items/RegistrationCommitteeDecisionsTable";
import { CollapsibleProvider } from "util/CollapsibleProvider";
import { DataGridWithHeader } from "common/DataGridWithHeader";
import {
  getReasonReleaseDecisionToFinalReviewIsDisabled,
  isRegComItemDecisionIncomplete,
  isReviewDecisionIncomplete
} from "decisions/decisionLogic";
import { useCurrentUser, Permissions } from "users";

const useStyles = makeStyles()((theme) => ({
  ...datagridStyles(theme),
  ...completeableRowStyles(theme),
  highDensityDataGridRoot: {
    "& .MuiDataGrid-columnHeader": {
      padding: "0 5px"
    },
    "& .MuiDataGrid-iconSeparator": {
      transform: "scale(0.5, 3)"
    }
  }
}));

interface Props {
  mode: "enter-committee-decisions" | "review-final-reports";
  registrationCommittees?: boolean;
}

const CommitteeDecisionsScreen: React.FunctionComponent<Props> = (props) => {
  const { classes, cx, theme } = useStyles();
  const history = useHistory();
  const location = useLocation();
  const notifications = useNotifications();
  const { fileDownloadAxios } = useAxios();
  const appConfig = useAppConfig();
  const { userHasPermission } = useCurrentUser();

  const [selectedMeetingId, setSelectedMeetingId] = useLocalStorage<number | null>("SelectedMeetingIdForCommitteeDecisions", null);
  const [enteringRegrets, setEnteringRegrets] = useState(false);
  const [finalReportsToApprove, setFinalReportsToApprove] = useState<PracticeReview[] | null>(null);
  const [finalReportPackageForPrIdDownloading, setFinalReportPackageForPrIdDownloading] = useState<number | null>(null);
  const [regenerateLettersDialogOpen, setRegenerateLettersDialogOpen] = useState(false);

  useEffect(() => {
    // Select the meeting whose ID is on the query string, if any
    const requestedMeetingId = new URLSearchParams(location.search).get("meetingId");
    if (requestedMeetingId) {
      setSelectedMeetingId(Number(requestedMeetingId));
    }
  }, []);

  const meetingsQuery = useQuery<
    { committeeMeetingsForFinalDecision: CommitteeMeeting[] },
    { forCoordinator: boolean; registrationCommittees: boolean }
  >(GetMeetingsForFinalDecisionQuery, {
    variables: {
      forCoordinator: props.mode === "enter-committee-decisions",
      registrationCommittees: props.registrationCommittees ?? false
    }
  });
  const meetings = meetingsQuery.data?.committeeMeetingsForFinalDecision ?? [];
  const selectedMeeting = selectedMeetingId ? meetings.find((m) => m.id === selectedMeetingId) : null;

  useEffect(() => {
    // Deselect the selected meeting if it's no longer present in the meeting list, which can occur after refreshing the query
    if (!meetingsQuery.loading && meetings.every((m) => m.id !== selectedMeetingId)) {
      setSelectedMeetingId(null);
    }

    // Select a meeting if it's the only one in the list.
    if (meetings.length === 1) {
      setSelectedMeetingId(meetings[0].id);
    }
  }, [meetings, meetingsQuery.loading, selectedMeetingId, setSelectedMeetingId]);

  const meetingWithFilesQuery = useQuery<{ committeeMeetingById: CommitteeMeeting }, { meetingId: number; meetingDate: string }>(
    FetchCommitteeMeetingToEnterDecisionsQuery,
    {
      variables: {
        meetingId: selectedMeeting?.id ?? 0,
        meetingDate: selectedMeeting?.meetingDate ?? ""
      },
      skip: !Boolean(selectedMeeting)
    }
  );
  const orderedMeetingPrs = orderMeetingPrs(meetingWithFilesQuery.data?.committeeMeetingById.assignedReviews ?? []);
  const tabledPrs = (meetingWithFilesQuery.data?.committeeMeetingById.tabledReviews ?? []).map((cmtr) => cmtr.practiceReview);
  const orderedTabledPrs = orderMeetingPrs(tabledPrs);
  const prsForSelectedMeeting = orderedMeetingPrs.concat(orderedTabledPrs);
  const prsReadyForFinalApproval = prsForSelectedMeeting.filter((pr) => pr.isFinalReportReviewedByDirector && !prIsCompleted(pr));

  const itemsForSelectedMeeting = meetingWithFilesQuery.data?.committeeMeetingById.assignedRegistrationCommitteeItems ?? [];
  const hasItemsInMeeting = itemsForSelectedMeeting.length > 0;

  const canApprovePracticeReviewFinalReports = !selectedMeeting?.isRegistrationCommittee && userHasPermission(Permissions.AdminTasks);

  const fillScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const sectionMinHeight =
    hasItemsInMeeting && prsForSelectedMeeting.length > 0
      ? fillScreen
        ? (window.innerHeight - 550) / 2
        : undefined
      : window.innerHeight - 450;

  function orderMeetingPrs(prs: PracticeReview[]) {
    return _.orderBy(
      prs,
      [
        (pr) => (pr.tabledFromPrevious ? pr.prNumber : 999999),
        (pr) => pr.mostAuthoritativeDecision?.isComply !== undefined,
        (pr) => pr.mostAuthoritativeDecision?.isComply ?? true,
        (pr) => pr.mostAuthoritativeDecision?.motionStandard?.sortOrder ?? 999,
        (f) => f.prNumber
      ],
      ["asc", "desc", "asc", "asc", "asc"]
    );
  }

  function committeeDecisionIsMissing(practiceReview: PracticeReview) {
    const decision = practiceReview.mostAuthoritativeDecision;
    return !decision || decision.decisionType.typeCode !== DecisionTypeCode.Committee;
  }

  function prIsCompleted(practiceReview: PracticeReview) {
    return practiceReview?.status?.baseStatusCode === PRBaseStatusCode.Completed;
  }

  const [generateDecisionLettersMutate, { loading: generatingDecisionLetters }] = useMutation<
    {
      committeeMeeting: Partial<CommitteeMeeting>;
    },
    {
      meetingId: number;
    }
  >(GenerateDecisionLettersMutation);

  async function generateDecisionLetters() {
    if (selectedMeetingId != null) {
      var result = await generateDecisionLettersMutate({
        variables: { meetingId: selectedMeetingId }
      });
      if (result.data?.committeeMeeting !== null) {
        notifications.success("Decision letters generated.");
      }
      setRegenerateLettersDialogOpen(false);
    }
  }

  async function handleGenerateLetters() {
    if (selectedMeetingId !== null) {
      if (selectedMeeting!.decisionLettersGenerated) {
        setRegenerateLettersDialogOpen(true);
      } else {
        await generateDecisionLetters();
      }
    }
  }

  const [toggleFinalReportReviewedMutate] = useMutation<
    {
      practiceReview: {
        toggleFinalReportReviewed: Partial<PracticeReview>;
        __typename?: string;
      };
    },
    {
      practiceReviewId: number;
      role: "Coordinator" | "Director";
    }
  >(ToggleFinalReportReviewedMutation);

  const [toggleTabledLetterReviewedMutate] = useMutation<
    {
      committeeMeetingTabledReview: {
        toggleTabledLetterReviewed: Partial<CommitteeMeetingTabledReview>;
        __typename?: string;
      };
    },
    {
      committeeMeetingId: number;
      practiceReviewId: number;
      role: "Coordinator" | "Director";
    }
  >(ToggleTabledLetterReviewedMutation);

  async function toggleReviewed(practiceReview: PracticeReview, role: "Coordinator" | "Director") {
    if (practiceReview.tabledToNext) {
      await toggleTabledLetterReviewedMutate({
        variables: {
          committeeMeetingId: practiceReview.tabledToNext.committeeMeetingId,
          practiceReviewId: practiceReview.tabledToNext.practiceReviewId,
          role: role
        },
        optimisticResponse: {
          committeeMeetingTabledReview: {
            toggleTabledLetterReviewed: {
              ...practiceReview.tabledToNext,
              isTabledLetterReviewedByCoordinator:
                role === "Coordinator"
                  ? !practiceReview.tabledToNext.isTabledLetterReviewedByCoordinator
                  : practiceReview.tabledToNext.isTabledLetterReviewedByCoordinator,
              isTabledLetterReviewedByDirector:
                role === "Director"
                  ? !practiceReview.tabledToNext.isTabledLetterReviewedByDirector
                  : practiceReview.tabledToNext.isTabledLetterReviewedByDirector
            },
            __typename: ""
          }
        }
      });
    } else {
      await toggleFinalReportReviewedMutate({
        variables: {
          practiceReviewId: practiceReview.id,
          role: role
        },
        optimisticResponse: {
          practiceReview: {
            toggleFinalReportReviewed: {
              ...practiceReview,
              isFinalReportReviewedByCoordinator:
                role === "Coordinator"
                  ? !practiceReview.isFinalReportReviewedByCoordinator
                  : practiceReview.isFinalReportReviewedByCoordinator,
              isFinalReportReviewedByDirector:
                role === "Director" ? !practiceReview.isFinalReportReviewedByDirector : practiceReview.isFinalReportReviewedByDirector
            },
            __typename: ""
          }
        }
      });
    }
  }

  const [releaseFinalReportsForDirectorReviewMutate, releaseFinalReportsForDirectorReviewMutation] = useMutation<
    {
      committeeMeeting: {
        releaseFinalReportsForDirectorReview: boolean;
      };
    },
    {
      meetingId: number;
    }
  >(ReleaseFinalReportsForDirectorReviewMutation);

  async function releaseForDirectorReview() {
    const result = await releaseFinalReportsForDirectorReviewMutate({
      variables: {
        meetingId: selectedMeetingId!
      },
      refetchQueries: [
        {
          query: GetMeetingsForFinalDecisionQuery,
          variables: { forCoordinator: true, registrationCommittees: props.registrationCommittees ?? false }
        },
        {
          query: FetchCommitteeMeetingToEnterDecisionsQuery,
          variables: {
            meetingId: selectedMeeting?.id ?? 0,
            meetingDate: selectedMeeting?.meetingDate ?? ""
          }
        }
      ]
    });

    if (result.data?.committeeMeeting.releaseFinalReportsForDirectorReview) {
      notifications.success(`Released to ${props.registrationCommittees ? "registrar" : "director"} review.`);
    }
  }

  const [approveFinalReportsMutate, approveFinalReportsMutation] = useMutation<
    {
      practiceReview: {
        approveFinalReports: PracticeReview[];
      };
    },
    {
      committeeMeetingId: number;
      prIdsToApproveFinalReports: number[];
      prIdsToApproveDecisionTabledLetters: number[];
    }
  >(ApproveFinalReportsMutation);

  async function approveFinalReports(practiceReviews: PracticeReview[]) {
    const result = await approveFinalReportsMutate({
      variables: {
        committeeMeetingId: selectedMeeting!.id,
        prIdsToApproveFinalReports: practiceReviews.filter((pr) => !pr.tabledToNext).map((pr) => pr.id!),
        prIdsToApproveDecisionTabledLetters: practiceReviews.filter((pr) => pr.tabledToNext).map((pr) => pr.id!)
      },
      refetchQueries: [
        {
          query: GetMeetingsForFinalDecisionQuery,
          variables: { forCoordinator: false, registrationCommittees: props.registrationCommittees ?? false }
        }
      ]
    });
    if ((result.data?.practiceReview?.approveFinalReports?.length ?? 0) > 0) {
      notifications.success(
        `PR${practiceReviews.length === 1 ? "" : "s"} ${practiceReviews.map((pr) => pr.prNumber).join(", ")} approved.`
      );
    }
  }

  async function downloadFinalReportPackage(pr: PracticeReview) {
    setFinalReportPackageForPrIdDownloading(pr.id);

    const getRequestEndpoint = `${appConfig.apiEndpoint}/api/practice-review-document/final-report-package?practiceReviewId=${pr.id}`;
    const filename = `Final Report Package - PR ${pr.prNumber}.pdf`;

    try {
      const getResult = await fileDownloadAxios.get(getRequestEndpoint);
      fileDownload(getResult.data, filename);
    } catch (error: any) {
      let finalError = error;

      // If 401, try once more
      if (error.response?.status === 401) {
        try {
          const retryResult = await fileDownloadAxios.get(getRequestEndpoint);
          fileDownload(retryResult.data, filename);
          return;
        } catch (retryError: any) {
          finalError = retryError;
        }
      }

      // Handle the error
      if (typeof finalError.response?.data === "string") {
        notifications.error(new Error(finalError.response.data));
      } else {
        notifications.error(finalError);
      }
    } finally {
      setFinalReportPackageForPrIdDownloading(null);
    }
  }
  const reasonDocumentsCannotBeGenerated = !selectedMeeting
    ? "No meeting is selected."
    : meetingWithFilesQuery.loading
    ? "Not all of the data for the meeting has finished loading."
    : itemsForSelectedMeeting.some((regComItem) => isRegComItemDecisionIncomplete(regComItem))
    ? "Some items in the meeting have incomplete decisions."
    : prsForSelectedMeeting.some((pr) => isReviewDecisionIncomplete(pr))
    ? "Some reviews in the meeting have incomplete committee decisions."
    : "";

  const canGenerateDocuments = !reasonDocumentsCannotBeGenerated;

  const ApproveFinalReportsWarningDialog = () => (
    <ConfirmationDialog
      open={!!finalReportsToApprove}
      title="Approve final reports and/or decision letters?"
      body="Once you approve a (non-tabled) PR, only the follow-up date can be changed on the Committee Decision. (For tabled PRs, the tabled decision letter will be sent to the firm.)"
      confirmButtonText="Continue"
      noDanger
      loading={approveFinalReportsMutation.loading}
      confirm={async () => {
        await approveFinalReports(finalReportsToApprove!);
        setFinalReportsToApprove(null);
      }}
      cancel={() => {
        setFinalReportsToApprove(null);
      }}
    />
  );

  const motionColumns: GridColumns<PracticeReview> = useMemo<GridColumns<PracticeReview>>(
    () =>
      selectedMeeting?.isRegistrationCommittee
        ? [] // we won't use a motion at all now that there are RCIs for the PPRP PRs
        : [
            {
              field: "motionStandard",
              headerName: "Standard Motion",
              headerClassName: classes.wrapHeader,
              width: 66,
              valueGetter: ({ row }) => row.mostAuthoritativeDecision?.motionStandard?.motionCode ?? "--"
            },
            {
              field: "motionPd",
              headerName: "PD Motion",
              headerClassName: classes.wrapHeader,
              width: 60,
              hide: !prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionPd?.motionCode),
              renderCell: ({ row }) => (
                <div
                  style={{ overflow: "hidden", whiteSpace: "break-spaces" }} // so that it wraps "PD-RECDIRECT" on the dash
                  title={row.mostAuthoritativeDecision?.motionPd?.shortDescription}>
                  {row.mostAuthoritativeDecision?.motionPd?.motionCode ?? "--"}
                </div>
              )
            },
            {
              field: "motionCpab",
              headerName: "CPAB Motion",
              headerClassName: classes.wrapHeader,
              width: 54,
              hide: !prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCpab?.motionCode),
              valueGetter: ({ row }) => row.mostAuthoritativeDecision?.motionCpab?.motionCode ?? "--"
            },
            {
              field: "motionCic",
              headerName: "CIC Motion",
              headerClassName: classes.wrapHeader,
              width: 54,
              hide: !prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCic?.motionCode),
              valueGetter: ({ row }) => row.mostAuthoritativeDecision?.motionCic?.motionCode ?? "--"
            }
          ],
    [
      selectedMeeting?.isRegistrationCommittee,
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionPd?.motionCode),
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCpab?.motionCode),
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCic?.motionCode)
    ]
  );

  const prReviewedColumns: GridColumns<PracticeReview> = useMemo<GridColumns<PracticeReview>>(
    () =>
      props.mode === "review-final-reports"
        ? props.registrationCommittees
          ? [] // we won't show the reviewed column for PPRP reviews, they will be handled on their RCIs
          : [
              {
                field: "isFinalReportReviewedByDirector",
                headerName: "Reviewed",
                headerAlign: "center",
                width: 72,
                align: "center",
                hide: !selectedMeeting?.decisionLettersGenerated,
                renderCell: ({ row }) => {
                  const practiceReview = row;
                  const reviewed = practiceReview.tabledToNext
                    ? practiceReview.tabledToNext.isTabledLetterReviewedByDirector
                    : practiceReview.isFinalReportReviewedByDirector;

                  return (
                    <Tooltip
                      title={
                        isReviewDecisionIncomplete(practiceReview)
                          ? "The committee decision is incomplete."
                          : prIsCompleted(practiceReview)
                          ? "The review is already completed."
                          : ""
                      }>
                      <span>
                        <Checkbox
                          checked={reviewed}
                          disabled={isReviewDecisionIncomplete(practiceReview) || prIsCompleted(practiceReview)}
                          onClick={() => toggleReviewed(practiceReview, "Director")}
                        />
                      </span>
                    </Tooltip>
                  );
                }
              }
            ]
        : [
            {
              field: "isFinalReportReviewedByCoordinator",
              headerName: "Ready to Release?",
              headerClassName: classes.wrapHeader,
              headerAlign: "center",
              width: 72,
              align: "center",
              hide: !selectedMeeting?.decisionLettersGenerated,
              renderCell: ({ row }) => {
                const practiceReview = row;
                const reviewed = practiceReview.tabledToNext
                  ? practiceReview.tabledToNext.isTabledLetterReviewedByCoordinator
                  : practiceReview.isFinalReportReviewedByCoordinator;

                // this can't be changed anymore once you've sent it off, which can happen one at a time for reg com,
                // but PRs don't know about that, they assume the whole meeting gets sent.  So we have to check for a
                // connected RCI and swap it out that way.
                // (however, there might be multiple items now, so we need to check the one for this meeting)
                const matchingItem = row.pprpProgramReviewRegComItems?.find((item) => item?.committeeMeeting?.id === selectedMeetingId);
                if (matchingItem?.isFinalReportSentForReview) {
                  return (
                    <Tooltip title="Already sent for final review">
                      <span>
                        <FontAwesomeIcon icon={faCheck} className={classes.alreadySent} />
                      </span>
                    </Tooltip>
                  );
                }

                return (
                  <Tooltip title={isReviewDecisionIncomplete(practiceReview) ? "The committee decision is incomplete." : ""}>
                    <span>
                      <Checkbox
                        checked={reviewed}
                        disabled={isReviewDecisionIncomplete(practiceReview) || practiceReview.isFinalReportReviewedByDirector}
                        onClick={() => toggleReviewed(practiceReview, "Coordinator")}
                      />
                    </span>
                  </Tooltip>
                );
              }
            }
          ],
    [props.mode, props.registrationCommittees, selectedMeeting?.decisionLettersGenerated, toggleReviewed]
  );
  const prTableColumns: GridColumns<PracticeReview> = useMemo<GridColumns<PracticeReview>>(
    () => [
      {
        field: "committeeTab",
        headerName: "Tab",
        width: 50,
        valueGetter: ({ row }) => (row.tabledToNext ? Number(row.tabledToNext.tab) : Number(row.committeeTab))
      },
      {
        field: "prNumber",
        headerName: "PR#",
        width: 70,
        renderCell: ({ row }) => {
          const practiceReview = row;
          return (
            <Link component={RouterLink} to={getRouteForPracticeReview(practiceReview)}>
              {practiceReview.prNumber}
            </Link>
          );
        }
      },
      {
        field: "firm.name",
        headerName: "Firm Name",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ row }) => row.firm?.name,
        renderCell: ({ row }) => (
          <Tooltip title={row.firm.name}>
            <span>{row.firm.name}</span>
          </Tooltip>
        )
      },
      {
        field: "tabledFromPrevious",
        headerName: "Tabled from Previous",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        cellClassName: classes.hideBooleanXsInCell,
        type: "boolean",
        width: 85,
        hide: !prsForSelectedMeeting.some((pr) => pr.tabledFromPrevious),
        renderCell: ({ row }) => (
          <Tooltip title={row.tabledFromPrevious?.reason ?? ""}>
            {row.tabledFromPrevious ? <GridCheckIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} /> : <></>}
          </Tooltip>
        )
      },
      {
        field: "assessment",
        headerName: "Assessment",
        width: 96,
        valueGetter: ({ row }) =>
          row.mostAuthoritativeDecision ? (row.mostAuthoritativeDecision!.isComply ? "Comply" : "Non-comply") : "--",
        cellClassName: (params: GridCellParams) =>
          cx({
            [classes.comply]: params.row.mostAuthoritativeDecision?.isComply === true,
            [classes.nonComply]: params.row.mostAuthoritativeDecision?.isComply === false
          })
      },
      {
        field: "zone",
        headerName: "Zone",
        width: 50,
        hide: !props.registrationCommittees,
        valueGetter: ({ row }) => row.mostAuthoritativeDecision?.complianceZone?.zoneCode ?? "--",
        renderCell: ({ row }) => (
          <Tooltip title={row.mostAuthoritativeDecision?.complianceZone?.name ?? ""}>
            <span className={row.mostAuthoritativeDecision?.complianceZone?.requiresResponse ? classes.nonComply : ""}>
              {row.mostAuthoritativeDecision?.complianceZone?.zoneCode ?? "--"}
            </span>
          </Tooltip>
        )
      },
      ...motionColumns,
      {
        field: "tabledToNext",
        headerName: "Tabled To Next",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        cellClassName: classes.hideBooleanXsInCell,
        type: "boolean",
        width: 70,
        hide: !prsForSelectedMeeting.some((pr) => pr.tabledToNext),
        renderCell: ({ row }) => (
          <Tooltip title={row.tabledToNext?.reason ?? ""}>
            {row.tabledToNext ? <GridCheckIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} /> : <></>}
          </Tooltip>
        )
      },
      {
        field: "decisionLetterLink",
        headerName: "Decision Letter",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 64,
        sortable: false,
        hide:
          props.registrationCommittees || // PPRP PRs will use the decision letter from their linked RCI now
          !selectedMeeting?.decisionLettersGenerated,
        renderCell: ({ row }) => {
          const practiceReview = row;
          if (props.registrationCommittees) {
            return (
              <Tooltip title="Please see the corresponding row in the other table above to access the decision letter.">
                <span style={{ color: theme.palette.text.secondary }}>See other</span>
              </Tooltip>
            );
          }
          return practiceReview.hasDecisionLetter && practiceReview.decisionLetterUrl ? (
            <MicrosoftWordLink href={practiceReview.decisionLetterUrl}>{prIsCompleted(practiceReview) ? "View" : "Edit"}</MicrosoftWordLink>
          ) : null;
        }
      },
      {
        field: "prReportLink",
        headerName: "PR Report",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 56,
        sortable: false,
        renderCell: ({ row }) => {
          const practiceReview = row;
          return practiceReview.practiceReviewReportUrl ? (
            <MicrosoftWordLink href={practiceReview.practiceReviewReportUrl}>
              {prIsCompleted(practiceReview) ? "View" : "Edit"}
            </MicrosoftWordLink>
          ) : null;
        }
      },
      {
        field: "pdDeclarationFormLink",
        headerName: "PD Declaration",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 82,
        sortable: false,
        hide:
          props.registrationCommittees ||
          !selectedMeeting?.decisionLettersGenerated ||
          !prsForSelectedMeeting.some((pr) => pr.pdDeclarationFormUrl),
        renderCell: ({ row }) => {
          const practiceReview = row;
          return practiceReview.pdDeclarationFormUrl ? (
            <MicrosoftWordLink href={practiceReview.pdDeclarationFormUrl}>Edit</MicrosoftWordLink>
          ) : null;
        }
      },
      {
        field: "pdProofOfAttendanceFormsLink",
        headerName: "PD Proof of Attendance Forms",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 82,
        sortable: false,
        hide:
          props.registrationCommittees ||
          !selectedMeeting?.decisionLettersGenerated ||
          !prsForSelectedMeeting.some((pr) => pr.pdProofOfAttendanceFormsUrl),
        renderCell: ({ row }) => {
          const practiceReview = row;
          return practiceReview.pdProofOfAttendanceFormsUrl ? <Link href={practiceReview.pdProofOfAttendanceFormsUrl}>Edit</Link> : null;
        }
      },
      {
        field: "finalReportLink",
        headerName: "Final Report Package",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 86,
        sortable: false,
        hide:
          props.registrationCommittees || // PPRP PRs will use the final report package from their linked RCI now
          !selectedMeeting?.decisionLettersGenerated,
        renderCell: ({ row }) => {
          const practiceReview = row;
          if (props.registrationCommittees) {
            return (
              <Tooltip title="Please see the corresponding row in the other table above to access the final report package.">
                <span style={{ color: theme.palette.text.secondary }}>See other</span>
              </Tooltip>
            );
          }
          return practiceReview.tabledToNext ? null : prIsCompleted(practiceReview) && practiceReview.finalReportPackageUrl ? (
            <Link href={practiceReview.finalReportPackageUrl}>View</Link>
          ) : finalReportPackageForPrIdDownloading === practiceReview.id ? (
            <CircularProgress size="small" />
          ) : (
            <Link component="button" variant="body2" onClick={() => downloadFinalReportPackage(practiceReview)}>
              View
            </Link>
          );
        }
      },
      {
        field: "editDecisionLink",
        headerName: "Edit Decision",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 62,
        sortable: false,
        renderCell: ({ row }) => {
          const practiceReview = row;
          return (
            <Tooltip
              title={
                committeeDecisionIsMissing(practiceReview)
                  ? "This review's director decision has not been signed off yet."
                  : isReviewDecisionIncomplete(practiceReview)
                  ? "This review requires additional information on its committee decision before its decision letter can be generated."
                  : ""
              }>
              <span>
                <IconButton
                  color={isReviewDecisionIncomplete(practiceReview) ? "error" : "primary"}
                  size="small"
                  disabled={committeeDecisionIsMissing(practiceReview)}
                  onClick={() =>
                    history.push(
                      getRouteForPracticeReview(practiceReview, PracticeReviewTabs.Decision, undefined, DecisionTypeCode.Committee)
                    )
                  }>
                  <FontAwesomeIcon icon={faArrowRight} />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      },
      ...prReviewedColumns,
      {
        field: "Approve",
        headerName: "",
        headerAlign: "center",
        align: "center",
        width: 120,
        sortable: false,
        hide:
          props.registrationCommittees ||
          !selectedMeeting?.decisionLettersGenerated ||
          props.mode === "enter-committee-decisions" ||
          !canApprovePracticeReviewFinalReports,
        renderCell: ({ row }) => {
          const practiceReview = row;
          if (props.registrationCommittees) {
            return (
              <Tooltip title="Actions available are in the corresponding row in the other table above.">
                <span style={{ color: theme.palette.text.secondary }}>See other</span>
              </Tooltip>
            );
          }
          if (practiceReview.tabledToNext?.decisionTabledLetterHasBeenSent) {
            return <Typography>Letter Sent</Typography>;
          } else
            return prIsCompleted(practiceReview) ? (
              <Typography>Completed</Typography>
            ) : (
              <Tooltip
                title={
                  (
                    practiceReview.tabledToNext
                      ? !practiceReview.tabledToNext.isTabledLetterReviewedByDirector
                      : !practiceReview.isFinalReportReviewedByDirector
                  )
                    ? "The PR must be marked as reviewed."
                    : ""
                }>
                <span>
                  <Button
                    variant="outlined"
                    disabled={
                      (practiceReview.tabledToNext
                        ? !practiceReview.tabledToNext.isTabledLetterReviewedByDirector
                        : !practiceReview.isFinalReportReviewedByDirector) || !canApprovePracticeReviewFinalReports
                    }
                    onClick={() => {
                      setFinalReportsToApprove([practiceReview]);
                    }}>
                    Approve
                  </Button>
                </span>
              </Tooltip>
            );
        }
      }
    ],
    [
      props.mode,
      selectedMeeting?.decisionLettersGenerated,
      prsForSelectedMeeting.some((pr) => pr.tabledToNext),
      prsForSelectedMeeting.some((pr) => pr.tabledFromPrevious),
      prsForSelectedMeeting.some((pr) => pr.pdDeclarationFormUrl),
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionPd?.motionCode),
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCpab?.motionCode),
      prsForSelectedMeeting.some((pr) => pr.mostAuthoritativeDecision?.motionCic?.motionCode)
    ]
  );
  const disableReleaseReason = getReasonReleaseDecisionToFinalReviewIsDisabled(
    meetingWithFilesQuery.loading,
    orderedMeetingPrs,
    orderedTabledPrs,
    props.registrationCommittees,
    itemsForSelectedMeeting
  );

  /** Moved this up here so that the same functional markup could be used to render the reviews on this page
   * if this is a PRC meeting, or passed in as a child to RegistrationCommitteeDecisionsTable if this is a
   * Reg Com meeting in order to tuck it in beneath the RCIs that IsPprpProgramReviewType. */
  const renderPRs = () => {
    return prsForSelectedMeeting?.length ? (
      <DataGridWithHeader
        title={
          hasItemsInMeeting ? (
            <Typography variant="h4" style={{ fontWeight: 300 }}>
              Related PPRP Program Reviews
            </Typography>
          ) : undefined
        }
        className={cx([classes.datagridRoot, classes.highDensityDataGridRoot])}
        deemphasizeHeader
        displayWithoutContainer
        allowHidingColumns
        disableSelectionOnClick
        loading={meetingWithFilesQuery.loading}
        rows={prsForSelectedMeeting}
        columns={prTableColumns}
        minHeight={props.registrationCommittees ? undefined : sectionMinHeight}
        pinnedColumns={{ left: ["committeeTab", "prNumber"] }}
        getRowClassName={(rowParams: GridRowClassNameParams<PracticeReview>) => {
          if (!props.registrationCommittees || !rowParams.row.pprpProgramReviewRegComItems?.length) {
            return "";
          }

          const matchingItem = rowParams.row.pprpProgramReviewRegComItems?.find((item) => item?.committeeMeeting?.id === selectedMeetingId);
          if (props.mode === "enter-committee-decisions") {
            return matchingItem?.isFinalReportSentForReview ? classes.completedRow : "";
          }
          if (props.mode === "review-final-reports") {
            return matchingItem?.isCompleted
              ? classes.completedRow
              : !matchingItem?.isFinalReportSentForReview
              ? classes.unactionableRow
              : "";
          }
          return "";
        }}
      />
    ) : null;
  };
  return (
    <CollapsibleProvider screen="CommitteeDecisions">
      <Helmet>
        <title>{`${props.mode === "enter-committee-decisions" ? "Enter Committee Decisions" : "Review Final Reports"} - PRS Online`}</title>
      </Helmet>

      <ApproveFinalReportsWarningDialog />

      <Stack sx={{ minHeight: "100%" }}>
        <ScreenHeader
          title={
            props.mode === "enter-committee-decisions"
              ? "Enter Committee Decisions"
              : props.registrationCommittees
              ? "Review Registration Committee Final Decisions"
              : "Review Final Reports"
          }
        />
        <Paper sx={{ p: 3, flexGrow: 1 }}>
          {meetings.length === 0 && !meetingsQuery.loading ? (
            <Typography>{`There are currently no meetings requiring committee decisions to be ${
              props.mode === "enter-committee-decisions" ? "entered" : "reviewed"
            }.`}</Typography>
          ) : (
            <Stack spacing={3}>
              <Stack direction="row" spacing={5} sx={{ justifyContent: "space-between" }}>
                <FormControl size="small" sx={{ width: "30em" }}>
                  <InputLabel id="meeting-select-label">Committee Meeting</InputLabel>
                  <Select
                    labelId="meeting-select-label"
                    value={selectedMeetingId ?? ""}
                    label="Committee Meeting"
                    onChange={(e) => setSelectedMeetingId(Number(e.target.value))}>
                    {meetings.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={2} alignItems="center">
                  {(props.mode === "enter-committee-decisions" || props.registrationCommittees) && (
                    <>
                      <Tooltip title={reasonDocumentsCannotBeGenerated}>
                        <span>
                          <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => setEnteringRegrets(true)}
                            disabled={!canGenerateDocuments}>
                            {selectedMeeting?.minutesGenerated ? "Regenerate" : "Generate"} Minutes
                          </Button>
                        </span>
                      </Tooltip>
                      {selectedMeeting?.minutesUrl && <MicrosoftWordLink href={selectedMeeting.minutesUrl}>Open Minutes</MicrosoftWordLink>}
                      <Tooltip title={reasonDocumentsCannotBeGenerated}>
                        <span>
                          <LoadingButton
                            variant="outlined"
                            color="primary"
                            loading={generatingDecisionLetters}
                            onClick={handleGenerateLetters}
                            disabled={!canGenerateDocuments}>
                            {selectedMeeting?.decisionLettersGenerated ? "Regenerate" : "Generate"} Decision Letters
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>

              {hasItemsInMeeting && (
                <RegistrationCommitteeDecisionsTable
                  itemsForSelectedMeeting={itemsForSelectedMeeting}
                  mode={props.mode}
                  selectedMeetingId={selectedMeetingId!}
                  decisionLettersGenerated={selectedMeeting!.decisionLettersGenerated}
                  renderPprpProgramReviews={renderPRs}
                />
              )}
              {selectedMeetingId && !hasItemsInMeeting && prsForSelectedMeeting.length > 0 && (
                <Stack spacing={2}>
                  <div style={{ width: "100%" }}>{renderPRs()}</div>
                </Stack>
              )}
              {meetingWithFilesQuery.loading && (
                <Stack direction="row" spacing={2} sx={{ alignSelf: "center" }}>
                  <CircularProgress />
                </Stack>
              )}
              <Stack direction="row" spacing={2} sx={{ alignSelf: "flex-end" }}>
                {selectedMeeting?.decisionLettersGenerated && props.mode === "enter-committee-decisions" && (
                  <Tooltip title={disableReleaseReason}>
                    <span>
                      <LoadingButton
                        color="primary"
                        variant="contained"
                        onClick={() => releaseForDirectorReview()}
                        loading={releaseFinalReportsForDirectorReviewMutation.loading}
                        disabled={disableReleaseReason !== ""}>
                        {props.registrationCommittees ? "Release Selected to Registrar" : "Release for Director Review"}
                      </LoadingButton>
                    </span>
                  </Tooltip>
                )}

                {!props.registrationCommittees &&
                  selectedMeeting?.decisionLettersGenerated &&
                  props.mode === "review-final-reports" &&
                  prsReadyForFinalApproval.length > 0 &&
                  canApprovePracticeReviewFinalReports && (
                    <>
                      <Button
                        color="primary"
                        variant="outlined"
                        disabled={meetingsQuery.loading || meetingWithFilesQuery.loading}
                        onClick={() => {
                          setFinalReportsToApprove(prsReadyForFinalApproval);
                        }}>
                        Approve All Reviewed PRs
                      </Button>
                    </>
                  )}
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>

      {enteringRegrets && selectedMeetingId && (
        <CommitteeMeetingRegretsDialog
          committeeMeetingId={selectedMeetingId}
          isRegistrationCommittee={selectedMeeting?.isRegistrationCommittee ?? false}
          isRegenerate={selectedMeeting?.minutesGenerated}
          onClose={() => setEnteringRegrets(false)}
        />
      )}

      <ConfirmationDialog
        open={regenerateLettersDialogOpen}
        title="Regenerate decision letters?"
        loading={generatingDecisionLetters}
        body={
          <>
            <DialogContentText>Do you want to regenerate decision letters for {selectedMeeting?.name}?</DialogContentText>
            <DialogContentText>
              <strong>
                This will regenerate documents in the{" "}
                {selectedMeeting?.isRegistrationCommittee ? "final decision package" : "final report package"}, discarding any manual
                changes made to them.
              </strong>
            </DialogContentText>
          </>
        }
        confirm={generateDecisionLetters}
        cancel={() => setRegenerateLettersDialogOpen(false)}
      />
    </CollapsibleProvider>
  );
};

export default CommitteeDecisionsScreen;
